import {useEffect, useState} from "react";

import {useFocusedFrom} from "./useFocusedFrom";
import {getFirstFocusableChild, getLastFocusableChild} from "./utils";

/**
 * When enabled, automatically focuses the first focusable child in scope
 * and does not allow focus to leave the scope by overwriting `Tab` key listeners.
 * @param enabled When false, focus behaves normally. When true, focus will be trapped to scope.
 */
export const useFocusTrap = (enabled: boolean) => {
  const [focusScope, setFocusScope] = useState<HTMLElement | null>(null);
  const {restoreFocus} = useFocusedFrom(focusScope);

  useEffect(() => {
    const firstFocusableChild = focusScope ? getFirstFocusableChild(focusScope) : null;
    const lastFocusableChild = focusScope ? getLastFocusableChild(focusScope) : null;

    if (focusScope && (!firstFocusableChild || !lastFocusableChild))
      throw Error("Focus scope does not contain focusable elements.");

    firstFocusableChild?.focus();

    const releaseFocus = (e: FocusEvent) => {
      if (!enabled) {
        restoreFocus(e);
      }
    };

    const preventWalkingOut = (e: KeyboardEvent) => {
      if (e.key === "Tab" && !e.shiftKey && lastFocusableChild && firstFocusableChild && enabled) {
        e.preventDefault();
        firstFocusableChild?.focus();
      }
    };

    const preventBackingOut = (e: KeyboardEvent) => {
      if (e.key === "Tab" && e.shiftKey && lastFocusableChild && firstFocusableChild && enabled) {
        e.preventDefault();
        lastFocusableChild?.focus();
      }
    };

    focusScope?.addEventListener("focusout", releaseFocus);
    lastFocusableChild?.addEventListener("keydown", preventWalkingOut);
    firstFocusableChild?.addEventListener("keydown", preventBackingOut);

    return () => {
      focusScope?.removeEventListener("focusout", releaseFocus);
      lastFocusableChild?.removeEventListener("keydown", preventWalkingOut);
      firstFocusableChild?.removeEventListener("keydown", preventBackingOut);
    };
  }, [enabled, focusScope, restoreFocus]);

  return setFocusScope;
};
